import { translate } from "app/language/service"
import { NOT_SAVED_ID } from "app/model"
import { getStore } from "app/store/store"
import { ICoordinate } from "coordinate/model"
import { coordinateRPO } from "coordinate/repository"
import {
  notifyCoordinateAdd,
  notifyCoordinateDelete,
  notifyCoordinatesDelete,
  notifyCoordinateUpdate
} from "coordinate/tools/common"
import { refreshInventory } from "form/inventory/tools/common"
import { DISTANCE_UNITS } from "form/map/model"
import { IForm, IFormChange, IFormCoordinate, IFormDataLoader } from "form/model"
import { formActionCreator } from "form/store/actions"
import { getFormData } from "form/store/state"
import { getForm, setFormGeoTargets, setFormRelations } from "form/tools/common"
import {
  getGeoTargetsChanges,
  getGeoTargetsForm,
  isImperialDistances,
  isValidRadius as isValidRadiusFn,
  setGeoTargetsForm,
  setGeoTargetsFormSelection,
  stopUpdatingGeoTargetsFormAfterDeletionIfNeeded
} from "form/tools/geo-targets"
import { geometrySVC } from "geometry/service"
import { messageSVC } from "message/service"
import { finalizeChange, getEmptyFormChange } from "./change"

export function getCoordinateChange(form: IForm): IFormChange {
  const change: IFormChange = getEmptyFormChange("campaign.geo-targets.tab.coordinate.title")
  const previousCoordinates = form.context.initialData ? form.context.initialData.geoTargets.coordinates : []
  for (const newCoordinate of form.geoTargets.coordinates) {
    const oldCoordinate = previousCoordinates.findByProperty("id", newCoordinate.id)
    if (!oldCoordinate) {
      change.addedItems.push(newCoordinate.name)
      continue
    }
    if (!areFormCoordinatesEquals(newCoordinate, oldCoordinate)) {
      change.updatedItems.push(newCoordinate.name)
    }
  }
  for (const oldCoordinate of previousCoordinates) {
    if (!form.geoTargets.coordinates.findByProperty("id", oldCoordinate.id)) {
      change.deletedItems.push(oldCoordinate.name)
    }
  }
  return finalizeChange(change)
}

export const coordinatesFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const coordinates = await coordinateRPO.getCoordinates(form.tempCampaignId)
    form.context.relations.coordinates = coordinates
    form.geoTargets.coordinates = coordinates
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Coordinates data-loader error ${String(error)}`)
  }
}

export function getRadiusInUserUnit(radiusInMeter: number): number {
  const toUnit = isImperialDistances() ? DISTANCE_UNITS.MILES : DISTANCE_UNITS.KILOMETERS
  const fromUnit = DISTANCE_UNITS.METERS
  return Math.round(geometrySVC.convertLength(radiusInMeter, toUnit, fromUnit))
}

export function getCoordinateRadiusInMeter(radiusInUserUnits?: string | null): number {
  if (!radiusInUserUnits) {
    radiusInUserUnits = getGeoTargetsForm().radius
  }
  const isValidRadius = isValidRadiusFn(radiusInUserUnits)
  if (!isValidRadius || radiusInUserUnits === null) {
    return 0
  }
  const toUnit = DISTANCE_UNITS.METERS
  const fromUnit = isImperialDistances() ? DISTANCE_UNITS.MILES : DISTANCE_UNITS.KILOMETERS
  return Math.round(geometrySVC.convertLength(Number(radiusInUserUnits), toUnit, fromUnit))
}

export async function addCoordinate(coordinate: IFormCoordinate): Promise<ICoordinate> {
  const form = getForm()
  const newFormCoordinates = form.geoTargets.coordinates.slice()
  coordinate.isProcessingServerSide = true
  newFormCoordinates.push(coordinate)
  getStore().dispatch(formActionCreator.setForm({ form: getNewFormForCoordinates(form, newFormCoordinates) }))
  try {
    const newCoordinate = await coordinateRPO.createCoordinate(form.tempCampaignId, coordinate)
    const newServerSideCoordinates = [...form.context.relations.coordinates, newCoordinate]
    coordinate.id = newCoordinate.id
    coordinate.latitude = newCoordinate.latitude
    coordinate.longitude = newCoordinate.longitude
    coordinate.radius = newCoordinate.radius
    coordinate.isProcessingServerSide = false
    const newForm = getNewFormForCoordinates(form, newFormCoordinates.slice(), newServerSideCoordinates)
    getStore().dispatch(
      formActionCreator.setForm({
        form: newForm,
        lastSavedData: getFormData(form),
        changes: {
          geoTargets: getGeoTargetsChanges(newForm)
        }
      })
    )
    refreshInventory()
    notifyCoordinateAdd(newCoordinate)
    return newCoordinate
  } catch (error) {
    console.error(error)
    // an error occured we set back coordinates
    getStore().dispatch(
      formActionCreator.setForm({ form: getNewFormForCoordinates(form, form.geoTargets.coordinates) })
    )
    messageSVC.error(translate("campaign.geo-targets.error.coordinate.add"))
    return Promise.reject("unable to create coordinate")
  }
}

export async function deleteCoordinate(coordinate: IFormCoordinate): Promise<void> {
  const form = getForm()
  coordinate.isProcessingServerSide = true
  const newFormCoordinates = form.geoTargets.coordinates.map(c => (c.id === coordinate.id ? coordinate : c))
  getStore().dispatch(formActionCreator.setForm({ form: getNewFormForCoordinates(form, newFormCoordinates) }))
  try {
    await coordinateRPO.deleteCoordinate(form.tempCampaignId, coordinate.id)
    const newForm = getNewFormForCoordinates(
      form,
      newFormCoordinates.filter(c => c.id !== coordinate.id),
      form.context.relations.coordinates.filter(c => c.id !== coordinate.id)
    )
    getStore().dispatch(
      formActionCreator.setForm({
        form: newForm,
        lastSavedData: getFormData(form),
        changes: {
          geoTargets: getGeoTargetsChanges(newForm)
        }
      })
    )
    const geoTargetsForm = getGeoTargetsForm()
    if (geoTargetsForm.selection) {
      // if coordinate is selected (in list multiple selection) we unselect it
      const selectedIds = geoTargetsForm.selection.coordinateIds
      if (selectedIds.length > 0 && selectedIds.includes(coordinate.id)) {
        getStore().dispatch(
          formActionCreator.setGeoTargetsForm(form.context.appContext, {
            selection: {
              ...geoTargetsForm.selection,
              coordinateIds: selectedIds.filter(id => id !== coordinate.id)
            }
          })
        )
      }
    }
    stopUpdatingGeoTargetsFormAfterDeletionIfNeeded([coordinate.id])
    refreshInventory()
    notifyCoordinateDelete(coordinate.id)
  } catch (error) {
    console.error("unable to delete coordinate", error)
    // an error occured we set back coordinates
    coordinate.isProcessingServerSide = false
    getStore().dispatch(
      formActionCreator.setForm({ form: getNewFormForCoordinates(form, form.geoTargets.coordinates) })
    )
    messageSVC.error(translate("campaign.geo-targets.error.coordinate.delete"))
  }
}

export function deleteCoordinates(coordinates: IFormCoordinate[]): Promise<void> {
  return new Promise(async resolve => {
    const form = getForm()
    const toDeleteCoordinateIds = coordinates.map(c => c.id)
    try {
      const newCoordinates = await coordinateRPO.setCoordinates(
        form.tempCampaignId,
        form.geoTargets.coordinates
          .filter(c => !toDeleteCoordinateIds.includes(c.id))
          .map(c => ({
            name: c.name,
            longitude: c.longitude,
            latitude: c.latitude,
            radius: c.radius
          }))
      )
      // update relations in store
      setFormRelations({
        coordinates: newCoordinates
      })
      // update coordinates in store, and add changes
      setFormGeoTargets({
        coordinates: form.geoTargets.coordinates.filter(c => !toDeleteCoordinateIds.includes(c.id))
      })
      stopUpdatingGeoTargetsFormAfterDeletionIfNeeded(toDeleteCoordinateIds)
      // update selection in store
      const { selection } = getGeoTargetsForm()
      setGeoTargetsFormSelection({
        coordinateIds: selection.coordinateIds.filter(id => !toDeleteCoordinateIds.includes(id))
      })
      notifyCoordinatesDelete(toDeleteCoordinateIds)
      resolve()
    } catch (error) {
      console.error(error)
      messageSVC.error(translate("campaign.geo-targets.delete.multiple.error"))
    }
  })
}

export async function updateCoordinate(coordinate: IFormCoordinate): Promise<void> {
  const form = getForm()
  coordinate = { ...coordinate }
  coordinate.isProcessingServerSide = true
  const newFormCoordinates = form.geoTargets.coordinates.map(c => (c.id === coordinate.id ? coordinate : c))
  getStore().dispatch(formActionCreator.setForm({ form: getNewFormForCoordinates(form, newFormCoordinates) }))
  try {
    await coordinateRPO.updateCoordinate(form.tempCampaignId, coordinate)
    coordinate.isProcessingServerSide = false
    const newForm = getNewFormForCoordinates(
      form,
      newFormCoordinates.map(c => (c.id === coordinate.id ? coordinate : c)),
      form.context.relations.coordinates.map(c => (c.id === coordinate.id ? coordinate : c))
    )
    getStore().dispatch(
      formActionCreator.setForm({
        form: newForm,
        lastSavedData: getFormData(form),
        changes: {
          geoTargets: getGeoTargetsChanges(newForm)
        }
      })
    )
    setGeoTargetsForm({
      toUpdateEntityId: NOT_SAVED_ID
    })
    refreshInventory()
    notifyCoordinateUpdate(coordinate)
  } catch (error) {
    console.error("unable to delete coordinate", error)
    // an error occured we set back coordinates
    getStore().dispatch(
      formActionCreator.setForm({ form: getNewFormForCoordinates(form, form.geoTargets.coordinates) })
    )
    messageSVC.error(translate("campaign.geo-targets.error.coordinate.update"))
  }
}

export function getNewFormForCoordinates(
  form: IForm,
  coordinates: IFormCoordinate[],
  relationCoordinates?: ICoordinate[]
): IForm {
  if (!relationCoordinates) {
    return {
      ...form,
      geoTargets: {
        ...form.geoTargets,
        coordinates
      }
    }
  } else {
    return {
      ...form,
      geoTargets: {
        ...form.geoTargets,
        coordinates
      },
      context: {
        ...form.context,
        relations: {
          ...form.context.relations,
          coordinates: relationCoordinates
        }
      }
    }
  }
}

function areFormCoordinatesEquals(coordinate1: IFormCoordinate, coordinate2: IFormCoordinate): boolean {
  return (
    coordinate1.name === coordinate2.name &&
    coordinate1.longitude === coordinate2.longitude &&
    coordinate1.latitude === coordinate2.latitude &&
    coordinate1.radius === coordinate2.radius
  )
}

export function hasCoordinates(form: IForm): boolean {
  return form.geoTargets.coordinates.length !== 0
}
